import { useTranslation } from "react-i18next";
import { SearchNormal1, Calendar2, Buildings, Category2, Add, Refresh2 } from "iconsax-react";
import { Button, Select, Input, DatePicker } from "@/shared/ui";
import type { DateRange } from "@/shared/utils";
import type { IncomeType, IncomePool } from "../../../types";

interface Props {
  search: string;
  onSearchChange: (value: string) => void;
  dateRange: DateRange;
  onDateRangeChange: (value: DateRange) => void;
  poolFilter: IncomePool | null;
  onPoolFilterChange: (value: IncomePool | null) => void;
  incomeTypeFilter: IncomeType | null;
  onIncomeTypeFilterChange: (value: IncomeType | null) => void;
  poolOptions: { label: string; value: IncomePool | null }[];
  incomeTypeOptions: { label: string; value: IncomeType | null }[];
  locale: "ru" | "kk" | "en";
  onCreateClick: () => void;
  canCreate: boolean;
  activeFilters: boolean;
  onResetFilters: () => void;
}

export default function IncomeFilter({
  search,
  onSearchChange,
  dateRange,
  onDateRangeChange,
  poolFilter,
  onPoolFilterChange,
  incomeTypeFilter,
  onIncomeTypeFilterChange,
  poolOptions,
  incomeTypeOptions,
  locale,
  onCreateClick,
  canCreate,
  activeFilters,
  onResetFilters,
}: Props) {
  const { t } = useTranslation("IncomePage");

  function handleDateRangeChange(v: Date | { start: Date | null; end: Date | null } | null) {
    if (!v || v instanceof Date) return;
    onDateRangeChange(v);
  }

  return (
    <div className="flex items-center justify-between gap-2 mb-7">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="min-w-48">
          <Input
            isSmall
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t("filters.searchPlaceholder")}
            icon={<SearchNormal1 size={14} color="currentColor" />}
          />
        </div>

        <DatePicker
          value={dateRange}
          mode="range"
          onChange={handleDateRangeChange}
          locale={locale}
          placeholder={t("filters.allTime")}
          isSmall
          hasPeriodPicker
          width="w-40"
          icon={<Calendar2 size={16} color="currentColor" />}
          align="left"
        />

        <div className="min-w-32">
          <Select<IncomeType | null>
            options={incomeTypeOptions}
            placeholder={t("filters.allIncomeTypes")}
            value={incomeTypeFilter}
            onChange={onIncomeTypeFilterChange}
            isSmall
            icon={<Category2 size={16} color="currentColor" />}
            width="w-80"
          />
        </div>

        <div className="min-w-32">
          <Select<IncomePool | null>
            options={poolOptions}
            placeholder={t("filters.allPools")}
            value={poolFilter}
            onChange={onPoolFilterChange}
            isSmall
            icon={<Buildings size={16} color="currentColor" />}
            width="w-48"
          />
        </div>

        {activeFilters && (
          <button
            className="px-3 h-8 flex justify-center items-center gap-2 content-base-low cursor-pointer"
            onClick={onResetFilters}>
            <Refresh2 size={16} color="currentColor" />
            <span className="text-label-sm">{t("filters.reset")}</span>
          </button>
        )}
      </div>

      <div className="flex items-center gap-3">
        {canCreate && (
          <Button variant="primary" size="md" onClick={onCreateClick}>
            <Add size={16} color="currentColor" />
            {t("header.createRecord")}
          </Button>
        )}
      </div>
    </div>
  );
}
